import { ConditionParams } from "@multiweb/clients/dataReader";
import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { Event } from "../../../../../Global/Base/Events";
import { IDropDownMenuItem } from "../../../../../UI/UI.Controls/DropDownMenu/DropDownInterfaces";
import { IFilterManager } from "./IFilterManager";

export type FilterBlockAttrNameToData = {
    dataType: string;
    locKey: string;
    locParams: string[];
    canFilterNull: boolean;
    canFilterCompare: boolean;
    canFilterEqual: boolean;
    canFilterLike: boolean;
};

export type FilterBlockAttrNameToDataMap = CaseInsensitiveMap<FilterBlockAttrNameToData>;

export interface IFilterBlock {
    /** Název formuláře */
    formName: string;
    /** Typ blocku, jestli je to blok z bocniho panelu nebo přímo na seznamu */
    type: FilterTargetType;
    /** Příznak, zda je filter implementační */
    isImplementationFilter: boolean;
    /**
     * Žádost o filtr
     * Pokud vkládáme hodnotu a je nastavena true - Volá filtr se zpoděním 500ms, běhěm té doby je možné tuto žádost zavolat opakovaně aniž by došlo
     * k zavolání více filtrování po sobě.
     */
    executeRequest: boolean;
    /**
     * IDentifikator filtru
     */
    filterId: string;
    /** Je zahrnut ve filtru */
    isUsed: boolean;
    /** Název filtru */
    filterName: string;
    objectName: string;

    /** Příznak, zda je komponenta v HTML DOMu */
    isConnected: boolean;

    /** Počet spuštění filtru */
    readonly executeCount: number;
    /**
     * Uložení filtru na server
     * @param newName Název pro uložení
     * @param saveAs Uložit jako nový filtr
     *
     * @return ID objektu
     */
    save(newName: string, filterManager: IFilterManager, saveAs?: boolean): Promise<IFilterBlock>;
    /** Nastaví jako neuložený */
    unsave(): void;
    /** Provede filtrování */
    execute(): Promise<any>;
    getCurrentFilterData(): Promise<IFilterBlockExecutedEventArgs>;
    /** Podmínka framu z definice. Slouží k tomu, aby při načítání a vyhledvání hodnot se nevraceli data, které nesplňují podmínku framu. */
    frameCondition?: string;
    frameConditionParams?: ConditionParams;
    /**
     * Vypnutí zkracování hodnot. Nastavením na true se do nabídky možných hodnot načtou nezkrácená data.
     * Tohle nastavení je přebíráno ze stejně pojmenovaného nastavení na frame.
     */
    disableValueShortening: boolean;
    groups: IFilterGroup[];
    filterGroupRestored: Event<IFilterGroup, void>;
    filterGroupRemoved: Event<IFilterGroup, {temporarily: boolean}>;
}

export interface IFilterBlockExecutedEventArgs {
    condition: string;
    filteredAttrNames: Map<string, boolean> | null;
    filterData: IFilterJsonData;
}

export interface IFilterGroup extends HTMLElement {
    parent: IFilterBlock;
    attrName: string;
    displayFormat: string;
    enabled: boolean;
    filterStructure: IFilterValue;
    createFilterItem(definition: IFilterItemDefinition, itemToReplace?: IFilterItem): void;
    items: IFilterItem[];
}

export interface IFilterItem extends HTMLElement {
    parent: IFilterBlock;
    group: IFilterGroup;
    currentSign: FilterJsonResultSign;
    isChanged: boolean;
    loadFromStructure(struct: IOperation, values?: { [id: string]: IFilterJsonResultValue }): void;
    getResult(omitDefaultAttrs: boolean, filterTargetType: FilterTargetType): IFilterJsonResult;
    isUsed: boolean;
}

/** Interface pro parser rozšířeného filtru */
export interface IExtendedFilterParser {
    /** Událost vyvolaná při chybě během parsování */
    readonly failed: Event<IExtendedFilterParser, string>;
    /**
     * Zpracuje vstup rozšířeného filtru do struktury pro vytváření filtrů
     * @param input Vstupní data
     * @param dataType Datový typ sloupce
     */
    parse(input: string | undefined | null, dataType: string): IExtendedFilterOperation[];
}

export class IExtendedFilterParser {
    private static _properties: (keyof IExtendedFilterParser)[] = ["failed"];
    private static _methods: (keyof IExtendedFilterParser)[] = ["parse"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface IFilterUserValue {
    userValue: any;
}

export class IFilterUserValue {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "userValue");
    }
}

export enum FilterTargetType {
    inPanel,
    column,
}

//#region Zakladni interface pro struktury
/**
 * Základní interface pro určení struktury pro datové typy
 */
export interface IFilterValue {
    dataType: string;
    groupList: Array<IGroupOperation>;
    multiOperation?: IMultiOperation;
    /** Zabraňuje možnosti přidat další operaci filtrování u položek, kde není potřeba */
    basicMultiOperation?: boolean;
}

/**
 * Interface sloužíci pro seskupení typu operací
 */
export interface IGroupOperation {
    /** Řazení skupin v zobrazeném dropdown menu */
    order?: number;
    visible?: boolean;
    operationList: Array<IOperation>;
}

/**
 * Základní popis jedné operace filtrování
 */
export interface IOperation {
    /** Identifikátor pro určení typu operace v daném datovém typu */
    id: string;
    /** Identifikator podrizene operace */
    subId: string | null;
    lockey: string;
    /** Popisek, pokud je hodnota zadana symbolem */
    captionLocKey?: string;
    /** Seznam polÍ, která bude filtrování pro zadání všech hodnot potřebovat */
    fieldList: Array<IFilterFieldInfo>;
    /** Řazení v dané skupině */
    order: number;
    /** Výchozí false [pokud není ani jeden, bere se jako první] [Operace, která je zobrazena jako výchozí po přídání datového typu] */
    isDefault?: boolean;
    isVisible?: boolean; // výchozí true
}

/**
 * Interface sloužíci pro seskupení typu operací
 */
export interface IMultiGroupOperation {
    /** Řazení skupin v zobrazeném dropdown menu */
    order?: number;
    visible?: boolean;
    operationList: Array<IMultiOneOperation>;
}

/**
 * Základní popis jedné operace filtrování
 */
export interface IMultiOneOperation {
    /** Identifikátor pro určení typu operace v daném datovém typu */
    id: string;
    lockey: string;
    /** Popisek, pokud je hodnota zadana symbolem */
    captionHtml?: string;
    /** Seznam poli, která bude filtrování pro zadání všech hodnot potřebovat */
    value: any;
    /** ŘazenÍ v dané skupinĚ */
    order: number;
    /** výchozí false pokud je true bude položka ve výchozím stavu vybrana */
    isDefault?: boolean;
    isVisible?: boolean; // výchozí true
}

/**
 * Základní popis multi operace filtrování (treba boolean)
 */
export interface IMultiOperation {
    /** Identifikátor pro určení typu operace v daném datovém typu */
    id: string;
    isVisible?: boolean; // výchozí true
    groupList: Array<IMultiGroupOperation>;
}

export class IMultiOperation {
    private static _properties: (keyof IMultiOperation)[] = ["id", "groupList"];
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Obsahuje informace o poli které je použito
 */
export interface IFilterFieldInfo {
    /** id pro identifikaci v návratové hodnotě */
    id: string;
    /** Textový popis typu pole */
    fieldType: string;
    /** Dodatečné informace */
    additional?: IFilterFieldInfoAdditional;
    /** Formátovací funkce, která upraví hodnotu při parsování z JSON */
    fromJsonFormatter?: (value: string) => string;
}

/**
 * Dodatečné informace k jednotlivým fieldům
 */
export interface IFilterFieldInfoAdditional {
    /** Informace o tom zda v datovém typu time zobrazí checkbox "včetně" */
    including?: boolean;
    defaultValue?: string;
}
//#endregion

//#region Interface pro zpracování Jsonu [výsledky z filtračního pole]
/**
 * Struktura, která bude výstupem z filtračních polí
 */
export interface IFilterJsonData {
    /** Informace o uložení podmínky, pokud je vyplněné bude se podmínka ukládat */
    saveInfo?: IFilterJsonSaveData;
    /** Indikator, zdali je ve výchozím stavu vybrán tento filtr (resp. pri poslednim použití formuláře) */
    enabled?: boolean;
    /** Název objektu v lf */
    componentId?: string;
    /** Seznam atributu z hodnotamy filtrů [Použití položek v podmínce (attrs[0]) AND (attrs[1])...] */
    attrs: Array<IFilterJsonDataAttr>;
    /** Pokročilá podmínka filtru */
    advancedCondition?: string;
}

export interface IFilterJsonDataAttr {
    /**Název sloupce */
    attrName: string;
    /**Logická spojka, která spojuje aktuální podmínku s předchozí (výchozí AND)*/
    operator?: FilterJsonResultSign;
    /**Příznak jestli je filtr aktivní */
    enabled?: boolean;
    /**Seznam podmínek použitých v atributu */
    conditions: Array<IFilterJsonResult>;
    /** Formátování dat */
    displayFormat?: string;
}

/**
 * Informacni struktura pro pripad ulozeni filtru
 */
export interface IFilterJsonSaveData {
    /** Pokud není vyplněné jedná se o novou podmínku, pokud ne, přepíše se podmínka s tímto id */
    id?: string;
    /** Nový název podmínky pro uložení */
    name: string;
}

/**
 * Výsledky filtrování pro daný attribut
 */
export interface IFilterJsonResult {
    /** ID odpovídajicí ID s typu IOperation */
    operation: string;
    subOperation?: string;
    /** Obsahuje podmínku, která spojuje aktuální podmínku s předchozí */
    operator: FilterJsonResultSign;
    values: Array<IFilterJsonResultValue>;
    enabled?: boolean;
    isValid?: boolean;
    isMultiItem?: boolean;
}

/**
 * Obsahuje ID fieldu a hodnotu z nej
 */
export interface IFilterJsonResultValue {
    /** ID odpovidajici ID s typu IFilterFieldInfo */
    fieldId: string;
    /** Typ pole, z duvodu konverze */
    fieldType: string;
    value: any;
    userValue?: any;
    /** Pokud byl pouzit additional, tak vraci hodnotu z nej */
    additional?: IFilterJsonResultValueAdditional;
    subOperationId?: string;
}

/**
 * Dodatečné informace k jednotlivým fieldům
 */
export interface IFilterJsonResultValueAdditional {
    /** Informace o tom zda v datovém typu time zobrazí checkbox "včetně" */
    including?: boolean;
}

/**
 * Obsahuje typ podmínky která spojuje dvě podmínky
 */
export type FilterJsonResultSign = "or" | "and";

/**
 * Obsahuje defaultní rozsah filtrování času
 */
export type FilterJsonResultSpan = "second" | "minute";

export enum FilterConvertFunction {
    json,
    sql,
}

/** Určuje, jaký typ filtrování je nastavený u Vyplněno/Nevyplněno */
export enum FilterEmptyType {
    empty,
    notEmpty,
}

export interface IFilterServerValueOperPrepare {
    condition: string;
    sign: FilterJsonResultSign;
}

export interface IFilterServerOperation {
    /** Určuje, zda se filtruje jako Vyplněno/Nevyplněno */
    emptyFilter?: FilterEmptyType;
    /** Podmínka, která se použije vždy před podmínkami s inputu. V podmínce lze použít keyword {attrName}, které bude nahrazeno jménem attributu */
    beginCondition?: string;
    /** Určuje, zda beginCondition neobsahuje žádné parametry pro hodnoty */
    noBeginValueParameters?: boolean;
    /** Určuje jestli se má operace použít pro hodnoty, které byly zkráceny. Pokud není definováno, použije se pro všechny. True pro zkrácené. False pro nezkrácené. */
    useForShortenedValues?: boolean;
    /** Podmínka, která se použije vždy po podmínkach s inputu */
    endCondition?: string;
    /** Seznam podmínek pro inputy */
    inputs?: { [id: string]: IFilterServerOperationInput };
    /** Operace  */
    subOperation?: { [id: string]: IFilterServerOperation };
}

export interface IFilterServerOperationInput {
    /** Podmínka */
    condition: string;
    /** Určení znaménka, pokud není určeno je použito OR */
    sign?: FilterJsonResultSign;
    /** Logický operátor pro vyhodnocení s beginCondition. Pokud není uvedeno, je použito AND. */
    signForBeginCondition?: FilterJsonResultSign;
    /** Určení dodatečných podmínek [pokud je použito je nahrazena podmínka s inputu] */
    additional?: IFilterServerOperationInputAdditional;
    /** Umožňuje podmínečně přidat další condition do filtru */
    conditionalOperation?: IFilterServerConditionalOperation[];
    /** Zda má být zabráněno escapování speciálních znaků */
    escapeValue?: boolean;
    /** Určuje zaokrouhlení při filtrovaní času */
    span?: FilterJsonResultSpan;
    /** Určuje jestli se má operace použít pro hodnoty, které byly zkráceny. Pokud není definováno, použije se pro všechny. True pro zkrácené. False pro nezkrácené. */
    useForShortenedValues?: boolean;
}

export interface IFilterServerOperationInputAdditional {
    [key: string]: string;
    including: string;
}

/** Obsahuje data k jedné programové podmínce pro rozšíření filtru */
export interface IFilterServerConditionalOperation {
    /** Podmínka, která je buď přidávaná do filtru nebo nahrazuje aktuální podmínku při splnění containsValue/equalsValue */
    condition: string;
    /** Určení znaménka, pokud není určeno je použito OR */
    sign?: FilterJsonResultSign;
    /** Pokud je naplněno, tak se použije k rozdělení vstupu do pole */
    separator?: string;
    /** Určuje, zda je podmínkou nahrazena původní podmínka nebo je přidána jako další podmínka */
    isForReplace?: boolean;
    /** Pokud je uvedená hodnota obsažena ve vstupu, dojde k přidání podmínky do filtru */
    containsValue?: string;
    //equalsValue?: string; // Pro budoucí použití
}

export interface IFieldRegistrator {
    control: any;
    from: (v: string, type: FilterConvertFunction) => any;
    to: (v: any, type: FilterConvertFunction) => string;
    afterCreate: (control: any) => void;
}

/** Obsahuje data potřebná pro vytvoření jedné položky filtru */
export interface IFilterItemDefinition {
    operation: IOperation | IMultiOperation;
    operator?: FilterJsonResultSign;
    values?: { [id: string]: IFilterJsonResultValue };
    /** Zda je položka filtru přidána jako aktivní */
    isUsed: boolean;
    subOperationID?: string;
}

/**
 * Interface, ktery musi byt obsahovat kazda skupina pro multifilter filter
 */
export interface IMultiOperationValueList {
    values: Array<IMultiOperationValue>;
    items: Array<IDropDownMenuItem>;
}

export interface IMultiOperationValue {
    id: string;
    fieldType: string;
    value: string;
    displayValue: string;
}

/** Výstupní interface rozšířeného filtru s daty pro vytvoření položek filtru */
export interface IExtendedFilterOperation {
    operator: FilterJsonResultSign;
    value: IFilterJsonResultValue;
}

//#endregion
/**
 * Položka nově vzniklého filtru
 */
export interface IReplaceItemData {
    /** Typ operace, název podle vnitřních struktůr daného typu */
    operation: string;
    /** Předaná hodnota */
    values?: { [id: string]: IFilterJsonResultValue };
    /** Operátor spojující následující podmínku */
    operator?: FilterJsonResultSign;
}

/**
 * Interface, který značí, že je možne nahradit současný filter jinou skladbou filtrů
 */
export interface IReplaceFilterItem {
    recreateFilter(operation: IOperation): IReplaceItemData[];
}

export class IReplaceFilterItem {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            typeof instance.recreateFilter === "function";
    }
}
